// Shared constants and helpers for rayteams-* DynamoDB tables accessed via
// callDynamoV2 (see ./ddb.js). Centralised so table names and sk key formats
// stay consistent across actions.

export const TABLE_RAYTEAMS_PRODUCT = 'rayteams-product';
export const TABLE_RAYTEAMS_LICENSE = 'rayteams-license';
export const TABLE_RAYTEAMS_PRODUCT_PACKAGE = 'rayteams-product-package';
// IMP-191 (2026-05-12): rayteams-manager-group — vouchers:default fallback 조회.
export const TABLE_RAYTEAMS_MANAGER_GROUP = 'rayteams-manager-group';
export const SK_VOUCHERS_DEFAULT = 'vouchers:default';

// sk key formats for rayteams-license table.
// Corp allocation record       : info:{corpId}
// Dealer allocation record     : info:{corpId}:{dealerId}
// Issued license record (lcuse): lcuse:{region}:{groupId}:{userId}[:suffix]
// Allocation audit event        : audit:{corpId}:{ts}:{operation}:{dealerId|self}
//                                 — FEAT-024 SPEC-RG-01/02 부여/할당/회수 이력
export const skCorpAllocation = (corpId) => `info:${corpId}`;
export const skDealerAllocation = (corpId, dealerId) => `info:${corpId}:${dealerId}`;
export const skDealerAllocationPrefix = (corpId) => `info:${corpId}:`;

// IMP-420 M4: sk 포맷 SSoT 와 co-locate — corp 원장(2-seg, `info:{mg}`) 판정 predicate.
// mg 가 빈 문자열/손상(`info:`, `info::`)이면 2-seg 이되 corp 원장이 아니므로 제외.
export const isCorpLedgerSk = (sk) => typeof sk === 'string' && /^info:[^:]+$/.test(sk);

// BUG-206 (PR #543 리뷰 Minor): 받은 레코드(3-seg, `info:{mg}:{dealerId}`) 양성 판정 predicate.
// 스코프 strict 여부를 `!isCorpLedgerSk(sk)` 로 뒤집으면 sk 부재·손상(`info:`, `info::`)이 strict 로
// 떨어져 `allocatedBy` 부재 행이 Σ(자식.t)에서 빠진다 → 천장 과대(over-issuance) 방향으로 fail-open.
// 3-seg 를 양성 판정해 미지값은 비-strict(부재 행 포함 = 보수적)로 남긴다.
export const isReceivedAllocationSk = (sk) => typeof sk === 'string' && /^info:[^:]+:[^:]+$/.test(sk);

// BUG-206 R3 (PR #543 리뷰 Minor): allocation sk 의 mg 세그먼트 추출 — 포맷 SSoT 와 co-location.
// 2-seg 원장(`info:{mg}`)과 3-seg 받은 레코드(`info:{mg}:{dealerId}`) 양쪽만 인정하고 그 외(부재·손상·
// 4-seg)는 null 이다. 종전에는 호출처마다 `split(':')[1]` 을 재구현해 경계가 서로 달랐다.
export const mgOfAllocationSk = (sk) => (
    isCorpLedgerSk(sk) || isReceivedAllocationSk(sk) ? String(sk).split(':')[1] : null
);

// Allocation audit sk format. corpId 를 두 번째 토큰에 두어 같은 법인의 모든
// 이력을 begins_with(audit:{corpId}:) 로 단일 prefix 쿼리할 수 있다.
//
// IMP-231 (2026-05-19): ts segment — ISO 8601 문자열 → 정수 ms (zero-pad 13자리).
//   - record body 의 ts 정수 필드와 일관성
//   - storage 효율 (string 24 chars → 13 chars)
//   - 정렬 안정성: zero-pad 자릿수 고정 → 사전식 = 시간순 보장
//   - 13자리 한계 = 9999999999999 ms = 2286-11-20T17:46:39.999Z (실용상 안전)
//   - legacy ISO sk record 마이그레이션은 scripts/migrations/2026-05-19_audit-sk-integer-timestamp.md
//
//   operation ∈ { 'grant' | 'allocate' | 'revoke' }
//     - 'grant'    : HQ → Corp 부여 (target = 'self')
//     - 'allocate' : Corp → Dealer 할당
//     - 'revoke'   : Corp ← Dealer 회수
export const AUDIT_TS_PAD_LENGTH = 13;
export const AUDIT_TS_MAX = 9999999999999;  // 2286-11-20T17:46:39.999Z — 13자리 한계

// PR #248 R1 (M2): fail-fast guard. audit log invariant 강제 — caller-discipline 대신
// 라이브러리 경계에서 검증. silent malformed sk 차단.
// PR #248 R2 (Arch Suggestion): TypeError vs RangeError 분리 — ECMA 규범 준수.
//   - TypeError: 타입 불일치 (number 아닌 값 — string/null/undefined/NaN/Symbol 등)
//   - RangeError: 타입 OK 이나 범위 이탈 (음수/14자리+/float)
// PR #248 R2 (CQ Suggestion): Symbol 은 String() / 비교 연산에서 TypeError throw 하므로
//   typeof 사전 거부로 일관된 에러 메시지 보장.
export const padAuditTs = (ts) => {
    // 타입 검증 — Symbol/null/undefined/string 등
    if (typeof ts !== 'number') {
        throw new TypeError(
            `padAuditTs: ts must be a number, got ${typeof ts}`,
        );
    }
    // NaN 은 typeof 가 'number' 라 별도 차단 (타입 오류로 분류)
    if (Number.isNaN(ts)) {
        throw new TypeError('padAuditTs: ts must not be NaN');
    }
    // 범위 검증 — 정수 + [0, AUDIT_TS_MAX]
    if (!Number.isInteger(ts) || ts < 0 || ts > AUDIT_TS_MAX) {
        throw new RangeError(
            `padAuditTs: ts must be integer in [0, ${AUDIT_TS_MAX}], got ${ts}`,
        );
    }
    return String(ts).padStart(AUDIT_TS_PAD_LENGTH, '0');
};

export const skAllocationAuditPrefix = (corpId) => `audit:${corpId}:`;
export const skAllocationAudit = (corpId, ts, operation, target) =>
    `audit:${corpId}:${padAuditTs(ts)}:${operation}:${target}`;
